import { MDText } from 'i18n-react';

import { getWordpress } from 'PublicAPI';

import { getParsedContent } from '../../../server/wordpress/parse/parse';
import { Author, GetAuthorsResponse, WPPageAPIResponse } from '../../containers/app/contentPages/wordPressPages/@types/Authors';
import { WordpressData } from '../../containers/app/contentPages/wordPressPages/@types/Wordpress';
import LocalTexts from '../../containers/app/contentPages/wordPressPages/components/GuideAuthorsContent/GuideAuthorsContent.text.json';

const LocalT = new MDText(LocalTexts);

const getAuthorJobTitle = (author: Author) => author?.yoast_head_json?.schema?.['@graph']?.find(
  (item) => item['@type'] === 'Person',
)?.jobTitle;

const getSocialLinks = (author: Author) => author?.yoast_head_json?.schema?.['@graph']?.find(
  (item) => item['@type'] === 'Person',
)?.sameAs || [];

const formatAuthorsData = ({ data }: {data: GetAuthorsResponse}): WordpressData => {
  const featuredAuthors = data.filter((author: Author) => author.acf?.featured_author)
    .map((author) => {
      const jobTitle = getAuthorJobTitle(author) ?? '';

      return {
        id: author.id,
        image: author.acf?.profile_image?.sizes?.medium || '',
        jobTitle,
        name: author.name,
      };
    });

  const wp = {
    archive: null,
    content: null,
    guideAuthors: featuredAuthors,
    meta: {
      desc: LocalT.translate('description') as string,
      title: LocalT.translate('title') as string,
    },
    posts: [],
    title: LocalT.translate('title') as string,
  };

  return wp;
};

const formatIndividualAuthorData = (author: Author, pages: WPPageAPIResponse[]): WordpressData => {
  const {
    acf: {
      featured_author: featuredAuthor, // eslint-disable-line camelcase
      profile_image: profileImage, // eslint-disable-line camelcase
    }, description, id, name,
  } = author;

  const jobTitle = getAuthorJobTitle(author) ?? '';
  const socialLinks = getSocialLinks(author);

  const formattedPages = pages
    .filter((page) => page._embedded?.author?.[0]) // eslint-disable-line no-underscore-dangle
    .map((page) => {
      const thumbnail = page._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.medium?.source_url ?? undefined; // eslint-disable-line no-underscore-dangle

      const url = page.link.replace('blog.motorway.co.uk', 'motorway.co.uk');

      const excerpt = page.yoast?.yoast_wpseo_metadesc || '';
      const decodedTitle = getParsedContent(page.title.rendered);

      return {
        excerpt,
        slug: page.slug,
        thumbnail,
        title: decodedTitle,
        url,
      };
    });

  const metaDesc = author?.description?.replace(/<(span|div).+?>/g, '')?.replace(/<\/(span|div)>/g, '')?.trim() ?? '';

  return {
    archive: null,
    author: {
      description,
      featured_author: featuredAuthor,
      id,
      jobTitle,
      name,
      socialLinks,
      thumbnail: profileImage?.sizes?.thumbnail,
    },
    content: author.description || '',
    meta: {
      desc: metaDesc,
      title: `${author.name} | Motorway Guides`,
    },
    posts: formattedPages,
    title: author.name,
  };
};

export const getGuideAuthorsData = async () => {
  const body: GetAuthorsResponse = await getWordpress('/wp/v2/users?per_page=100', false, null);

  return formatAuthorsData({ data: body });
};

export const getAuthorDetailsData = async (authorId?: string): Promise<WordpressData> => {
  const emptyWp: WordpressData = {
    archive: null,
    content: null,
    meta: {
      desc: null,
      title: null,
    },
    posts: [],
    title: null,
  };

  if (!authorId) {
    return emptyWp;
  }

  const [authorResponse, pagesResponse] = await Promise.allSettled([
    getWordpress(`/wp/v2/users/${authorId}`, false, null) as Promise<Author>,
    getWordpress(`/wp/v2/pages?author=${authorId}&parent=32&_embed=true`, false, null) as Promise<WPPageAPIResponse[]>,
  ]);

  if (authorResponse.status === 'rejected' || !authorResponse?.value?.acf?.featured_author) {
    return emptyWp;
  }

  const pagesData = pagesResponse.status === 'fulfilled' ? pagesResponse.value : [];

  return formatIndividualAuthorData(authorResponse.value, pagesData);
};
